/**
 * Support Page
 * 
 * Public page (no authentication required)
 * Contact information and support channels
 */

import React from "react";
import { PublicLayout } from "../components/public/PublicLayout";
import "./LegalPages.css";

export function SupportPage(): JSX.Element {
  return (
    <PublicLayout>
      <div className="legal-page">
      <div className="legal-page__container">
        <h1>Need help?</h1>
        
        <section className="legal-page__section">
          <h2>Common Support Topics</h2>
          <ul>
            <li>Payment verification issues</li>
            <li>Account access / Auth0 email change request</li>
            <li>Exchange connection problems</li>
          </ul>
          <p>
            <strong>Open a support ticket:</strong> Log in to your account and use the Support tab to create a support ticket.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>1. In-App Support Tickets</h2>
          <p>
            <strong>For registered users:</strong> Log in to your account and use the Support tab to create a support ticket. 
            This is the fastest way to get help with:
          </p>
          <ul>
            <li>Trading execution issues</li>
            <li>API key problems</li>
            <li>Payment questions</li>
            <li>Account issues</li>
          </ul>
          <p>
            When creating a ticket, please include:
          </p>
          <ul>
            <li>Execution Job ID (if applicable)</li>
            <li>Payment Intent ID (if applicable)</li>
            <li>Detailed description of the issue</li>
            <li>Screenshots (if helpful)</li>
          </ul>
        </section>

        <section className="legal-page__section">
          <h2>2. Email Support</h2>
          <p>
            For general inquiries or if you cannot access your account:
          </p>
          <p>
            <strong>Email:</strong> support@zakyam.ai
          </p>
          <p>
            Please include your account email and a detailed description of your issue.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>3. Response Times</h2>
          <p>
            We aim to respond to support requests within:
          </p>
          <ul>
            <li><strong>Urgent issues:</strong> 2-4 hours</li>
            <li><strong>High priority:</strong> 4-8 hours</li>
            <li><strong>Normal priority:</strong> 24-48 hours</li>
            <li><strong>Low priority:</strong> 48-72 hours</li>
          </ul>
        </section>

        <section className="legal-page__section">
          <h2>4. Common Issues</h2>
          <h3>API Key Problems</h3>
          <ul>
            <li>Ensure your API key has Trade permissions only (no Withdraw)</li>
            <li>Check that IP whitelisting is configured correctly (if enabled)</li>
            <li>Verify the API key is active on your exchange account</li>
          </ul>

          <h3>Trading Not Executing</h3>
          <ul>
            <li>Check that trading is enabled in your account settings</li>
            <li>Verify you have sufficient balance</li>
            <li>Check for any system-wide trading restrictions</li>
          </ul>

          <h3>Payment Issues</h3>
          <ul>
            <li>Verify the transaction hash (tx_hash) is correct</li>
            <li>Check that you sent to the correct wallet address</li>
            <li>Ensure you sent the correct amount (including network fees)</li>
            <li>Wait for required confirmations (usually 12+ for Ethereum)</li>
          </ul>
        </section>

        <section className="legal-page__section">
          <h2>5. Before Contacting Support</h2>
          <p>Please check:</p>
          <ul>
            <li>This Support page for common solutions</li>
            <li>The Risk Disclaimer for trading risks</li>
            <li>Your account settings and trading status</li>
            <li>Exchange status and API connectivity</li>
          </ul>
        </section>

        <section className="legal-page__section">
          <h2>6. Security Notice</h2>
          <p>
            <strong>Never share your API keys, passwords, or private keys with anyone.</strong> 
            Our support team will never ask for these credentials. If someone requests them, it's a scam.
          </p>
        </section>

        <section className="legal-page__section">
          <h2>7. Feedback</h2>
          <p>
            We welcome your feedback! If you have suggestions for improving the Service, please let us know through 
            a support ticket or email.
          </p>
        </section>
      </div>
      </div>
    </PublicLayout>
  );
}

export default SupportPage;

